import './GenericCombobox.css'
import { Fragment, useState, JSX } from 'react'
import {
  Combobox,
  Transition,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption
} from '@headlessui/react'
import { Label } from '../InputLabel/InputLabel'
import { inputClasses, InputSize } from '../Input/Input'
import { InputVariant } from '../InputGroup/InputGroup'
import cx from 'classnames'
import { CaretDownIcon, CircleXIcon } from '../icons'
import Check from '../icons/CheckCircle/CheckCircle'

function normalise(str: string) {
  return str
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
}

// Lightweight Levenshtein distance implementation for fuzzy matching
function levenshtein(firstString: string, secondString: string): number {
  const firstLength = firstString.length
  const secondLength = secondString.length
  if (firstLength === 0) {
    return secondLength
  }
  if (secondLength === 0) {
    return firstLength
  }

  const matrix: number[][] = Array.from({ length: firstLength + 1 }, () =>
    new Array(secondLength + 1).fill(0)
  )

  for (let rowIndex = 0; rowIndex <= firstLength; rowIndex++) {
    matrix[rowIndex][0] = rowIndex
  }
  for (let columnIndex = 0; columnIndex <= secondLength; columnIndex++) {
    matrix[0][columnIndex] = columnIndex
  }

  for (let rowIndex = 1; rowIndex <= firstLength; rowIndex++) {
    for (let columnIndex = 1; columnIndex <= secondLength; columnIndex++) {
      const cost = firstString[rowIndex - 1] === secondString[columnIndex - 1] ? 0 : 1
      matrix[rowIndex][columnIndex] = Math.min(
        matrix[rowIndex - 1][columnIndex] + 1,
        matrix[rowIndex][columnIndex - 1] + 1,
        matrix[rowIndex - 1][columnIndex - 1] + cost
      )
    }
  }

  return matrix[firstLength][secondLength]
}

function generateInputClasses(sizeType: InputSize, className?: string) {
  return cx('combobox-input', inputClasses.fixed, inputClasses.sizeToClasses[sizeType], className)
}

const defaultOptionRender = ({
  selected,
  disabled,
  displayValue
}: {
  disabled?: boolean
  selected: boolean
  displayValue: string
}) => {
  return disabled ? (
    <div className="combobox-option-content-disabled">
      <span className="combobox-option-label-disabled">{displayValue}</span>
    </div>
  ) : (
    <div className="combobox-option-content">
      <span className="combobox-option-label">{displayValue}</span>
      {selected ? (
        <span className="text-green-400">
          <Check className="combobox-check-icon" aria-hidden="true" />
        </span>
      ) : null}
    </div>
  )
}

interface GenericComboboxProps<T> {
  items: T[]
  value: T | null
  onChange: (value: T | null) => void
  displayValue?: (item: T) => string
  label?: string
  placeholder?: string
  disabled?: boolean
  clearable?: boolean
  sizeType?: InputSize
  variant?: InputVariant
  className?: string
  required?: boolean
  tooltip?: React.ReactNode
  tooltipPlacement?: 'top' | 'bottom'
  tooltipSize?: 'small' | 'medium' | 'large'
  hint?: React.ReactNode
  error?: string
  noItemsMessage?: string
  renderOption?: (props: {
    selected: boolean
    item: T | null
    focus: boolean
    disabled?: boolean
    displayValue: string
  }) => JSX.Element
}

type ScoredItem<T> = { item: T; score: number }

export function GenericCombobox<T extends { value: string; label: string; disabled?: boolean }>({
  items,
  value,
  onChange,
  displayValue = (item: T) => item.label,
  label,
  placeholder = '',
  disabled = false,
  clearable = false,
  sizeType = 'md',
  required = false,
  tooltip,
  tooltipPlacement = 'top',
  tooltipSize = 'small',
  className,
  hint,
  error,
  noItemsMessage,
  renderOption = defaultOptionRender
}: GenericComboboxProps<T>) {
  const [query, setQuery] = useState('')

  const showNoItemsMessage = items.length === 0 && noItemsMessage

  const filteredItems = (() => {
    const normalizedQuery = normalise(query)
    if (!normalizedQuery) {
      return items
    }

    const scored: ScoredItem<T>[] = items
      .map(item => {
        const normalizedLabel = normalise(displayValue(item))

        if (normalizedLabel.includes(normalizedQuery)) {
          return { item, score: 0 }
        }

        const distances = normalizedLabel
          .split(/[^a-z0-9]+/i)
          .map(token => levenshtein(token, normalizedQuery))
        const minDistance = Math.min(...distances)

        return { item, score: minDistance }
      })
      .filter(({ score }) => score <= 2)

    return scored.sort((sort1, sort2) => sort1.score - sort2.score).map(({ item }) => item)
  })()
  return (
    <div>
      {label && (
        <Label
          className="mb-1.5 inline-flex items-center gap-1"
          required={required}
          tooltip={tooltip}
          tooltipPlacement={tooltipPlacement}
          tooltipSize={tooltipSize}
        >
          {label}
        </Label>
      )}
      <Combobox
        immediate
        virtual={showNoItemsMessage ? undefined : { options: filteredItems }}
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        <div className="relative">
          <ComboboxInput
            className={cx(
              generateInputClasses(sizeType, className),
              clearable && 'pr-14',
              error && 'border-red-500 focus:border-red-500 focus:ring-red-500'
            )}
            displayValue={(item: T | null) => (item ? displayValue(item) : '')}
            onChange={event => setQuery(event.target.value)}
            placeholder={placeholder}
            title={value?.label}
            aria-invalid={error ? true : undefined}
          />
          {clearable && value != null && !disabled ? (
            <button
              type="button"
              className="combobox-clear"
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                onChange(null)
              }}
              aria-label="Clear selection"
            >
              <CircleXIcon className="combobox-clear-icon" aria-hidden="true" />
            </button>
          ) : null}
          <ComboboxButton className="combobox-button">
            <CaretDownIcon className="combobox-icon" aria-hidden="true" />
          </ComboboxButton>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-1"
          enter="transition ease-in duration-100"
          enterFrom="opacity-0 -translate-y-1"
          enterTo="opacity-100 translate-y-0"
          afterLeave={() => setQuery('')}
        >
          {showNoItemsMessage ? (
            <ComboboxOptions anchor="bottom" className="combobox-options">
              <div className="combobox-no-items">
                <span className="combobox-no-items-text">{noItemsMessage}</span>
              </div>
            </ComboboxOptions>
          ) : (
            <ComboboxOptions anchor="bottom" className="combobox-options">
              {({ option }) => (
                <ComboboxOption
                  key={option.value}
                  disabled={option?.disabled}
                  className="combobox-option"
                  value={option}
                  title={option.label}
                >
                  {({ selected, focus }) =>
                    renderOption({
                      item: option,
                      selected,
                      focus,
                      disabled: option?.disabled,
                      displayValue: displayValue(option)
                    })
                  }
                </ComboboxOption>
              )}
            </ComboboxOptions>
          )}
        </Transition>
      </Combobox>
      {hint && <label className="combobox-hint">{hint}</label>}
      {error && (
        <p className="mt-1.5 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
